import { type ReactNode, memo, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/atoms/dialog";
import { Button } from "~/atoms/button";
import { Checkbox } from "~/atoms/checkbox";
import { cn } from "~/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";
import DatePicker from "~/molecules/DatePicker";
import { formatDate } from "~/helpers/date";
import { toggleSetItem } from "~/helpers/set";

export interface ApplyDocument {
  id: number;
  title: string;
  reviewFrom: Date | null;
  reviewTo: Date | null;
}

export interface ApplyStep {
  name: string;
  order: number;
}

export interface DueDateRow {
  reviewFrom: Date | null;
  reviewTo: Date | null;
  stepDueDates: (Date | null)[];
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateName: string;
  steps: ApplyStep[];
  documents: ApplyDocument[];
  onApply: (rows: Record<number, DueDateRow>) => void;
  isApplying: boolean;
}

export function ApplyTemplateDatesDialog({
  open,
  onOpenChange,
  templateName,
  steps,
  documents,
  onApply,
  isApplying,
}: Props) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <ApplyTemplateDatesDialogContent
          templateName={templateName}
          steps={steps}
          documents={documents}
          onApply={onApply}
          isApplying={isApplying}
          onOpenChange={onOpenChange}
        />
      )}
    </Dialog>
  );
}

interface DateCellProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  className: string;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  errorNode: ReactNode;
}

const DateCell = memo(function DateCell({
  value,
  onChange,
  className,
  onMouseEnter,
  onMouseLeave,
  errorNode,
}: DateCellProps) {
  return (
    <TableCell className="px-3 py-2">
      <div
        className={className}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <DatePicker value={value} onChange={(date) => onChange(date ?? null)} />
      </div>
      {errorNode}
    </TableCell>
  );
});

interface DocumentDateRowProps {
  doc: ApplyDocument;
  row: DueDateRow;
  steps: ApplyStep[];
  datesConfirmed: boolean;
  isSelected: boolean;
  rowErrors: Map<number, "past" | "order"> | undefined;
  onUpdateCell: (
    field: "reviewFrom" | "reviewTo" | number,
    value: Date | null,
  ) => void;
  onToggleSelection: () => void;
  onHoverCell: (col: number) => void;
  onHoverLeave: () => void;
  isBulkHighlighted: (col: number) => boolean;
}

const DocumentDateRow = memo(function DocumentDateRow({
  doc,
  row,
  steps,
  datesConfirmed,
  isSelected,
  rowErrors,
  onUpdateCell,
  onToggleSelection,
  onHoverCell,
  onHoverLeave,
  isBulkHighlighted,
}: DocumentDateRowProps) {
  const { t } = useTranslation();

  if (datesConfirmed) {
    return (
      <TableRow>
        <TableCell className="px-3 py-2 font-medium">{doc.title}</TableCell>
        <TableCell className="text-muted-foreground px-3 py-2">
          {formatDate(row.reviewFrom)}
        </TableCell>
        {steps.map((step, stepIndex) => (
          <TableCell
            key={step.order}
            className="text-muted-foreground px-3 py-2"
          >
            {formatDate(row.stepDueDates[stepIndex])}
          </TableCell>
        ))}
        <TableCell className="text-muted-foreground px-3 py-2">
          {formatDate(row.reviewTo)}
        </TableCell>
      </TableRow>
    );
  }

  const errorMsg = (col: number) => {
    const err = rowErrors?.get(col);
    if (!err) return null;
    return (
      <p className="text-destructive text-xs">
        {err === "past"
          ? t(($) => $.workflowTemplates.applyDatesDialog.errorNotInPast)
          : t(($) => $.workflowTemplates.applyDatesDialog.errorAfterPrevious)}
      </p>
    );
  };

  const cellClasses = (col: number) =>
    cn(
      "rounded-md",
      rowErrors?.has(col) && "ring-2 ring-destructive",
      !rowErrors?.has(col) &&
        isBulkHighlighted(col) &&
        "bg-primary/10 ring-2 ring-primary",
    );

  return (
    <TableRow>
      <TableCell className="px-3 py-2">
        <Checkbox
          checked={isSelected}
          onCheckedChange={onToggleSelection}
          aria-label={t(($) => $.commons.buttons.selectItem, {
            name: doc.title,
          })}
        />
      </TableCell>
      <TableCell className="px-3 py-2 font-medium">{doc.title}</TableCell>
      <DateCell
        value={row.reviewFrom}
        onChange={(date) => onUpdateCell("reviewFrom", date)}
        className={cellClasses(0)}
        onMouseEnter={() => onHoverCell(0)}
        onMouseLeave={onHoverLeave}
        errorNode={errorMsg(0)}
      />
      {steps.map((step, stepIndex) => (
        <DateCell
          key={step.order}
          value={row.stepDueDates[stepIndex] ?? null}
          onChange={(date) => onUpdateCell(stepIndex, date)}
          className={cellClasses(stepIndex + 1)}
          onMouseEnter={() => onHoverCell(stepIndex + 1)}
          onMouseLeave={onHoverLeave}
          errorNode={errorMsg(stepIndex + 1)}
        />
      ))}
      <DateCell
        value={row.reviewTo}
        onChange={(date) => onUpdateCell("reviewTo", date)}
        className={cellClasses(steps.length + 1)}
        onMouseEnter={() => onHoverCell(steps.length + 1)}
        onMouseLeave={onHoverLeave}
        errorNode={errorMsg(steps.length + 1)}
      />
    </TableRow>
  );
});

type ContentProps = Omit<Props, "open">;

function ApplyTemplateDatesDialogContent({
  templateName,
  steps,
  documents,
  onApply,
  isApplying,
  onOpenChange,
}: ContentProps) {
  const { t } = useTranslation();

  const [datesConfirmed, setDatesConfirmed] = useState(false);

  const [rows, setRows] = useState<Map<number, DueDateRow>>(() => {
    const map = new Map<number, DueDateRow>();
    for (const doc of documents) {
      map.set(doc.id, {
        reviewFrom: doc.reviewFrom,
        reviewTo: doc.reviewTo,
        stepDueDates: steps.map(() => null),
      });
    }
    return map;
  });
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const updateCell = useCallback(
    (
      docId: number,
      field: "reviewFrom" | "reviewTo" | number,
      value: Date | null,
    ) => {
      setRows((prev) => {
        const next = new Map(prev);

        const targetIds =
          selectedIds.has(docId) && selectedIds.size > 1
            ? [...selectedIds]
            : [docId];

        for (const targetId of targetIds) {
          const row = next.get(targetId);
          if (!row) continue;

          if (typeof field === "number") {
            const newStepDueDates = [...row.stepDueDates];
            newStepDueDates[field] = value;
            next.set(targetId, { ...row, stepDueDates: newStepDueDates });
          } else {
            next.set(targetId, { ...row, [field]: value });
          }
        }

        return next;
      });
    },
    [selectedIds],
  );

  const toggleSelection = useCallback((docId: number) => {
    setSelectedIds((prev) => toggleSetItem(prev, docId));
  }, []);

  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (prev.size === documents.length) {
        return new Set();
      }
      return new Set(documents.map((d) => d.id));
    });
  }, [documents]);

  const allSelected =
    documents.length > 0 && selectedIds.size === documents.length;

  const [hoveredCell, setHoveredCell] = useState<{
    docId: number;
    col: number;
  } | null>(null);

  const isBulkHighlighted = useCallback(
    (docId: number, col: number) =>
      hoveredCell !== null &&
      hoveredCell.col === col &&
      selectedIds.size > 1 &&
      selectedIds.has(hoveredCell.docId) &&
      selectedIds.has(docId),
    [hoveredCell, selectedIds],
  );

  const validationErrors = useMemo(() => {
    const errors = new Map<number, Map<number, "past" | "order">>();
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const toMidnight = (d: Date) => {
      const m = new Date(d);
      m.setHours(0, 0, 0, 0);
      return m.getTime();
    };

    for (const [docId, row] of rows) {
      const dated: { col: number; time: number }[] = [];

      if (row.reviewFrom)
        dated.push({ col: 0, time: toMidnight(row.reviewFrom) });
      for (let i = 0; i < row.stepDueDates.length; i++) {
        const d = row.stepDueDates[i];
        if (d) dated.push({ col: i + 1, time: toMidnight(d) });
      }
      if (row.reviewTo)
        dated.push({ col: steps.length + 1, time: toMidnight(row.reviewTo) });

      if (dated.length === 0) continue;

      const rowErrors = new Map<number, "past" | "order">();

      if (dated[0]!.time < todayMidnight.getTime()) {
        rowErrors.set(dated[0]!.col, "past");
      }

      for (let i = 1; i < dated.length; i++) {
        if (dated[i]!.time < dated[i - 1]!.time) {
          rowErrors.set(dated[i]!.col, "order");
        }
      }

      if (rowErrors.size > 0) {
        errors.set(docId, rowErrors);
      }
    }

    return errors;
  }, [rows, steps.length]);

  const hasValidationErrors = validationErrors.size > 0;

  const handleApply = useCallback(() => {
    onApply(Object.fromEntries(rows));
  }, [rows, onApply]);

  return (
    <DialogContent className="w-fit max-w-[90vw] sm:max-w-[90vw]">
      <DialogHeader>
        <DialogTitle>{templateName}</DialogTitle>
        <DialogDescription>
          {datesConfirmed
            ? t(($) => $.workflowTemplates.applyDatesDialog.confirmDescription)
            : t(($) => $.workflowTemplates.applyDatesDialog.description)}
        </DialogDescription>
      </DialogHeader>

      <Table
        className="border-collapse"
        wrapperClassName="w-max max-w-[calc(90vw-2rem)] max-h-[60vh] rounded-md border"
      >
        <TableHeader className="sticky top-0 z-10">
          <TableRow>
            {!datesConfirmed && (
              <TableHead className="w-10 px-3 py-2">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  aria-label={t(($) => $.commons.buttons.selectAll)}
                />
              </TableHead>
            )}
            <TableHead className="min-w-50 px-3 py-2">
              {t(($) => $.workflowTemplates.applyDatesDialog.documentTitle)}
            </TableHead>
            <TableHead className="min-w-45 px-3 py-2">
              {t(($) => $.commons.document.reviewFrom)}
            </TableHead>
            {steps.map((step) => (
              <TableHead key={step.order} className="min-w-45 px-3 py-2">
                {step.name}
              </TableHead>
            ))}
            <TableHead className="min-w-45 px-3 py-2">
              {t(($) => $.commons.document.reviewTo)}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => {
            const row = rows.get(doc.id);
            if (!row) return null;

            return (
              <DocumentDateRow
                key={doc.id}
                doc={doc}
                row={row}
                steps={steps}
                datesConfirmed={datesConfirmed}
                isSelected={selectedIds.has(doc.id)}
                rowErrors={validationErrors.get(doc.id)}
                onUpdateCell={(field, value) =>
                  updateCell(doc.id, field, value)
                }
                onToggleSelection={() => toggleSelection(doc.id)}
                onHoverCell={(col) => setHoveredCell({ docId: doc.id, col })}
                onHoverLeave={() => setHoveredCell(null)}
                isBulkHighlighted={(col) => isBulkHighlighted(doc.id, col)}
              />
            );
          })}
        </TableBody>
      </Table>

      <DialogFooter>
        {datesConfirmed ? (
          <>
            <Button
              variant="outline"
              onClick={() => setDatesConfirmed(false)}
              disabled={isApplying}
            >
              {t(($) => $.commons.buttons.back)}
            </Button>
            <Button onClick={handleApply} disabled={isApplying}>
              {isApplying
                ? t(($) => $.workflowTemplates.applying)
                : t(($) => $.workflowTemplates.applyDatesDialog.apply)}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t(($) => $.commons.buttons.cancel)}
            </Button>
            <Button
              onClick={() => setDatesConfirmed(true)}
              disabled={hasValidationErrors}
            >
              {t(($) => $.workflowTemplates.applyDatesDialog.confirmDates)}
            </Button>
          </>
        )}
      </DialogFooter>
    </DialogContent>
  );
}
